import { useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "../providers/supabase/supabase";

/**
 * Modo manutenção pelo banco (migration 295). O deploy que quebra o site liga
 * antes das migrations e desliga quando o site novo está no ar
 * (scripts/deploy-main.sh --manutencao).
 *
 * Fica em volta do app inteiro, e não no Layout: Hub, seguros, Canopus e North
 * IA são rotas noLayout. Passam sempre: o super admin da North e as telas de
 * entrada (login, senha), para dar para entrar durante a manutenção.
 *
 * Sem a função no banco (site antes da 295) ou sem rede: segue o app normal.
 */

export interface EstadoManutencao {
  ativa: boolean;
  mensagem: string | null;
}

const INTERVALO_MS = 60_000;
const ROTAS_LIVRES = /^#?\/(login|forgot-password|set-password|sign-up)\b/;

export const rotaLivre = (hash: string) => ROTAS_LIVRES.test(hash);

const lerManutencao = async (): Promise<EstadoManutencao> => {
  const { data, error } = await supabase.rpc("plataforma_manutencao" as never);
  const d = (error ? null : data) as { ativa?: boolean; mensagem?: string | null } | null;
  return { ativa: d?.ativa === true, mensagem: d?.mensagem ?? null };
};

const souSuperAdmin = async () => {
  const { data: sessao } = await supabase.auth.getSession();
  if (!sessao.session) return false;
  const { data } = await supabase.rpc("is_super_admin" as never);
  return data === true;
};

export const PortaoManutencao = ({ children }: { children: ReactNode }) => {
  const [estado, setEstado] = useState<EstadoManutencao | null>(null);
  const [liberado, setLiberado] = useState<boolean | null>(null);
  const [hash, setHash] = useState(() => window.location.hash);
  const estavaAtiva = useRef(false);

  useEffect(() => {
    const aoMudar = () => setHash(window.location.hash);
    window.addEventListener("hashchange", aoMudar);
    return () => window.removeEventListener("hashchange", aoMudar);
  }, []);

  useEffect(() => {
    let vivo = true;
    const conferir = () =>
      lerManutencao().then((e) => {
        if (!vivo) return;
        // Acabou a manutenção: recarrega para pegar o site novo.
        if (estavaAtiva.current && !e.ativa) window.location.reload();
        estavaAtiva.current = e.ativa;
        setEstado(e);
      }, () => vivo && setEstado({ ativa: false, mensagem: null }));
    void conferir();
    const id = window.setInterval(conferir, INTERVALO_MS);
    window.addEventListener("focus", conferir);
    return () => {
      vivo = false;
      window.clearInterval(id);
      window.removeEventListener("focus", conferir);
    };
  }, []);

  useEffect(() => {
    if (!estado?.ativa) return;
    let vivo = true;
    void souSuperAdmin().then((s) => vivo && setLiberado(s), () => vivo && setLiberado(false));
    const { data } = supabase.auth.onAuthStateChange(() => {
      void souSuperAdmin().then((s) => vivo && setLiberado(s), () => vivo && setLiberado(false));
    });
    return () => {
      vivo = false;
      data.subscription.unsubscribe();
    };
  }, [estado?.ativa]);

  if (!estado?.ativa || rotaLivre(hash)) return <>{children}</>;
  if (liberado === null) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#354660]" />
      </div>
    );
  }
  if (liberado) return <>{children}</>;
  return <PaginaManutencao mensagem={estado.mensagem} />;
};

export const PaginaManutencao = ({ mensagem }: { mensagem: string | null }) => {
  const sair = async () => {
    await supabase.auth.signOut();
    window.location.hash = "#/login";
  };
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50 px-6">
      <div className="w-full max-w-md text-center">
        <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#354660]/10">
          <Wrench className="h-7 w-7 text-[#354660]" />
        </div>
        <h1 className="mb-3 text-2xl font-semibold text-[#354660]">Plataforma em manutenção</h1>
        <p className="mb-8 leading-relaxed text-gray-500">
          {mensagem ?? "Atualização em andamento. Retorno em alguns minutos."}
        </p>
        <Button variant="outline" onClick={() => void sair()}
          className="border-[#354660]/30 text-[#354660] hover:bg-[#354660]/5">
          Sair
        </Button>
      </div>
    </div>
  );
};
